/**
 * RV9q · the layer between a product and a version: the product's master records, each with
 * the version in force.
 *
 *   <FamilyList>   inside a product card — one line per record: name, code, effective version
 *                  (or why there is none), how many versions; click to open that record's versions.
 *   <VersionStrip> above a record's version cards — every version in order with its state,
 *                  the one in force highlighted; click a version to jump to it.
 *
 * Both read the served `families` payload (designer/families/) and compose nothing: which
 * version is effective is the server's derivation, never a guess made here.
 */
import React from "react";
import { Tag, Tooltip, Typography } from "antd";
import { CheckCircle2, CircleDashed, FileClock, History, Archive } from "lucide-react";
import { formatDate } from "@/utils/datetime";

const { Text } = Typography;

/** One word per lifecycle state, and the colour it wears. */
const STATE = {
  finalized: { label: "Finalised", color: "green" },
  approved: { label: "Approved", color: "green" },
  process_approved: { label: "Approved", color: "gold" },
  under_review: { label: "Under review", color: "blue" },
  partially_submitted: { label: "Under review", color: "blue" },
  returned: { label: "Returned for correction", color: "orange" },
  draft: { label: "Draft", color: "default" },
};

export function stateOf(v) {
  if (!v) return { label: "—", color: "default" };
  if (v.archived) return { label: "Archived", color: "default" };
  if (v.effective) return { label: "Effective", color: "green" };
  if (v.superseded) return { label: "Superseded", color: "default" };
  return STATE[v.state] || { label: v.state || "Draft", color: "default" };
}

function VersionIcon({ v, size = 13 }) {
  if (v.archived) return <Archive size={size} />;
  if (v.effective) return <CheckCircle2 size={size} />;
  if (v.superseded) return <History size={size} />;
  if (v.state === "process_approved") return <CheckCircle2 size={size} style={{ opacity: .6 }} />;
  if (v.state === "under_review" || v.state === "partially_submitted") return <FileClock size={size} />;
  return <CircleDashed size={size} />;
}

/** The families of one product, inside its card. `mbmrAlias` is the plant's word for a record. */
export function FamilyList({ families = [], mbmrAlias = "MBMR", onOpen, muted, ink }) {
  if (!families.length) return null;
  return (
    <div className="mbmr-families" data-testid="family-list" onClick={(e) => e.stopPropagation()}>
      <div className="mbmr-families-head" style={{ color: muted }}>
        {families.length} {mbmrAlias}{families.length === 1 ? "" : "s"} · {families.filter((f) => f.effective).length} in force
      </div>
      {families.map((f) => {
        const eff = f.effective;
        const why = eff
          ? `${eff.version} in force${eff.valid_from ? ` since ${formatDate(eff.valid_from)}` : ""}`
          : f.awaiting_finalisation ? `${f.latest.version} approved — awaiting finalisation`
          : f.under_review ? `${f.latest.version} under review — nothing in force yet`
          : f.drafts ? `${f.latest.version} draft — nothing in force yet`
          : "nothing in force";
        return (
          <button type="button" key={f.root_id} className="mbmr-family" data-testid="family-row"
                  onClick={() => onOpen?.(f)} title={`Open the versions of ${f.name}`}>
            <span className="mbmr-family-name" style={{ color: ink }}>
              {f.is_packaging ? <Tag style={{ marginInlineEnd: 6 }}>MBPR</Tag> : null}{f.name}
              {f.batch_size ? <small> · {Number(f.batch_size)} {f.batch_uom}</small> : null}
            </span>
            <span className="mbmr-family-meta" style={{ color: muted }}>
              <code>{f.code}</code>
              <span className={`mbmr-family-eff ${eff ? "on" : ""}`}>
                <VersionIcon v={eff || f.latest} size={12} /> {why}
              </span>
              <span>{f.count} version{f.count === 1 ? "" : "s"}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Every version of one record, in order, the effective one lit. */
export function VersionStrip({ family, currentId, onPick, onClear, mbmrAlias = "MBMR" }) {
  if (!family) return null;
  return (
    <div className="mbmr-versions" data-testid="version-strip">
      <div className="mbmr-versions-head">
        <span className="t">{family.name}</span>
        <code>{family.code}</code>
        <span className="n">{family.count} version{family.count === 1 ? "" : "s"}</span>
        {family.effective
          ? <Tag color="green">in force: {family.effective.version}</Tag>
          : <Tag>nothing in force</Tag>}
        {onClear ? <button type="button" className="mbmr-versions-clear" onClick={onClear}>All {mbmrAlias}s of this product</button> : null}
      </div>
      <ol className="mbmr-versions-list">
        {family.versions.map((v) => {
          const st = stateOf(v);
          const on = String(v.id) === String(currentId);
          const when = v.effective && v.valid_from ? `since ${formatDate(v.valid_from)}`
            : v.superseded && v.valid_to ? `until ${formatDate(v.valid_to)}` : "";
          return (
            <li key={v.id} className={`${v.effective ? "eff" : ""} ${on ? "on" : ""}`}>
              <Tooltip title={`${v.code} · ${v.is_production ? "Production" : "Trial"}${when ? ` · ${when}` : ""}`}>
                <button type="button" onClick={() => onPick?.(v)} data-testid="version-pill" aria-current={on ? "true" : undefined}>
                  <VersionIcon v={v} />
                  <b>{v.version}</b>
                  <Tag color={st.color} style={{ marginInlineEnd: 0 }}>{st.label}</Tag>
                  {when ? <Text type="secondary" style={{ fontSize: 11 }}>{when}</Text> : null}
                </button>
              </Tooltip>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

/** `{product_id: {families, mbmrs, effective}}` from the served list. */
export function indexFamilies(rows = []) {
  const out = {};
  rows.forEach((r) => { out[r.product_id] = r; });
  return out;
}

/** The family a version belongs to, or null. */
export function familyOfVersion(byProduct, productId, recipeId) {
  const fams = byProduct?.[productId]?.families || [];
  return fams.find((f) => f.versions.some((v) => String(v.id) === String(recipeId))) || null;
}
